import React, {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import FlowsNode from './flowsNode/flowsNode';
import ReactFlow, { MiniMap, Controls, Background, Panel, useNodesState, useEdgesState, addEdge } from 'reactflow';
import generateEdgesByNodeIds from './generateEdges';
import autoLayout from './autoLayout';
import checkAndInsertNode from './flowEdges/autoInsertEdge';
import FlowsPreviewPanel from '../flowsPreviewPanel/flowsPreviewPanel';
import FlowsPreviewDetail from '../flowsPreviewDetail/flowsPreviewDetail';
import { initialNodeDataAbstract } from './flowsNode/initialData';
import './style.css';
import 'reactflow/dist/style.css';
import BranchingNode from './flowsNode/BranchingNode';
import EndNode from './flowsNode/endNode';

const FlowsPreview = () => {
    const containerRef = useRef(null);
    // Add a ref to store the ReactFlow instance
    const reactFlowInstanceRef = useRef(null);
    
    // Node Layout Variables
    const [layoutType, setLayoutType] = useState('vertical');
    const [layoutOptions, setLayoutOptions] = useState({
      startX: 250,
      startY: 50,
      xGap: 100,
      yGap: 100
    });

    // Node variables
    const initialNodes = useMemo(() => 
        autoLayout(initialNodeDataAbstract, layoutType, layoutOptions), 
        [layoutType, layoutOptions]
    );
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    
    // Edge Variables
    const initialEdgesFromNodes = useMemo(() => 
        generateEdgesByNodeIds(initialNodes),  
        [initialNodes]
    );
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdgesFromNodes);
    
    // Current User Selection Variables
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [selectedAction, setSelectedAction] = useState('rectangle');
    
    // required to maintain our custom shape feature.
    // type of node should be flowNode for this to work. 
    const nodeTypes = useMemo(() => ({
        flowNode: FlowsNode,
        branchingNode: BranchingNode,
        endNode: EndNode
    }), []);

    // Function to check if a node is close to an edge and insert it if needed
    const checkAndInsertNodeHandler = useCallback((draggedNode) => {
        checkAndInsertNode(draggedNode, nodes, edges, setNodes, setEdges);
    }, [edges, nodes, setEdges, setNodes]);
    
    // Function to move a node and all of its children together
    const moveNodeWithChildren = useCallback((draggedNode, dx, dy) => {
        if (!draggedNode.data.children || draggedNode.data.children.length <= 1) {
            return; // Only apply for nodes with multiple children
        }
        
        // We need to disable ReactFlow's built-in node movement for the dragged node
        // and handle it ourselves to keep it synchronized with the children
        
        // Temporarily store the current nodes array
        const currentNodes = [...nodes];
        
        // Find the dragged node in our current array (to get its latest position)
        const currentDraggedNode = currentNodes.find(n => n.id === draggedNode.id);
        if (!currentDraggedNode) return;
        
        // Create a map for faster node lookups
        const nodeMap = new Map(currentNodes.map(node => [node.id, node]));
        
        // Collect all nodes that need to be updated (including the main dragged node)
        const nodesToUpdate = new Map();
        nodesToUpdate.set(draggedNode.id, {
            ...currentDraggedNode
        });
        
        // Process all children
        const processAllChildren = (parentId) => {
            const parent = nodeMap.get(parentId);
            if (!parent || !parent.data.children) return;
            
            parent.data.children.forEach(childId => {
                const child = nodeMap.get(childId);
                if (child && !nodesToUpdate.has(childId)) {
                    nodesToUpdate.set(childId, {
                        ...child,
                        position: {
                            x: child.position.x + dx,
                            y: child.position.y + dy
                        }
                    });
                    processAllChildren(childId);
                }
            });
        };
        
        // Start processing from the dragged node
        processAllChildren(draggedNode.id);
        
        // Update all nodes
        const updatedNodes = currentNodes.map(node => 
            nodesToUpdate.has(node.id) ? nodesToUpdate.get(node.id) : node
        );
        
        setNodes(updatedNodes);
    }, [nodes, setNodes]);
    
    // Save the last position of dragged node to calculate delta
    const lastDragPositionRef = useRef(null);
    
    // Handler for node drag
    const onNodeDrag = useCallback((event, node) => {
        // Prevent the default drag behavior
        event.preventDefault();
        
        // Check for insertion during drag
        checkAndInsertNodeHandler(node);
        
        // Calculate position delta if we have a previous position
        if (lastDragPositionRef.current) {
            const dx = node.position.x - lastDragPositionRef.current.x;
            const dy = node.position.y - lastDragPositionRef.current.y;
            
            // Get all nodes that need to move together
            const nodesToMove = new Set([node.id]);
            
            // If this node has multiple children, move them too
            if (node.data.children && node.data.children.length > 1) {
                // Collect all descendant nodes
                const collectDescendants = (nodeId) => {
                    const currentNode = nodes.find(n => n.id === nodeId);
                    if (!currentNode || !currentNode.data.children) return;
                    
                    currentNode.data.children.forEach(childId => {
                        nodesToMove.add(childId);
                        collectDescendants(childId);
                    });
                };
                
                // Start collecting from the dragged node
                node.data.children.forEach(childId => {
                    nodesToMove.add(childId);
                    collectDescendants(childId);
                });
            }
            
            // Update all nodes that need to move
            const updatedNodes = nodes.map(n => {
                if (nodesToMove.has(n.id)) {
                    return {
                        ...n,
                        position: {
                            x: n.position.x + dx,
                            y: n.position.y + dy
                        }
                    };
                }
                return n;
            });
            
            setNodes(updatedNodes);
        }
        
        // Update last known position
        lastDragPositionRef.current = { ...node.position };
    }, [checkAndInsertNodeHandler, nodes, setNodes]);
    
    // Update node drag start handler to initialize position tracking
    const onNodeDragStart = useCallback((event, node) => {
        lastDragPositionRef.current = { ...node.position };
    }, []);
    
    // Node drag stop handler
    const onNodeDragStop = useCallback((event, node) => {
        // Check for insertion on drag stop
        checkAndInsertNodeHandler(node);
        
        // Reset the position reference
        lastDragPositionRef.current = null;
    }, [checkAndInsertNodeHandler]);

    // Hook to change the layout positioning
    useEffect(() => {
      if (containerRef.current) {
        const updateDimensions = () => {
          const { width, height } = containerRef.current.getBoundingClientRect();
          
          if (layoutType === 'horizontal') {
            setLayoutOptions(prev => ({
              ...prev,
              startX: 300,
              startY: height / 2,
              xGap: 250,
              yGap: 250
            }));
          } else {
            setLayoutOptions(prev => ({
              ...prev,
              startX: width / 2,
              startY: 50,
              xGap: 250,
              yGap: 150
            }));
          }
        };
        
        updateDimensions();
        
        window.addEventListener('resize', updateDimensions);
        
        return () => window.removeEventListener('resize', updateDimensions);
      }
    }, [containerRef, layoutType]);

    // Hook to apply the layout positioning and have ReactFlow re-render it
    useEffect(() => {
      const newNodes = autoLayout(
        nodes.map(node => ({ ...node, position: undefined })),
        layoutType,
        layoutOptions
      );
      // reseting nodes and edges for ReactFlow 
      // to re-render based on new position
      setNodes(newNodes);
      setEdges(generateEdgesByNodeIds(newNodes));

    }, [layoutType, layoutOptions, setNodes, setEdges]);

    // Function to center view on a selected node
    const centerViewOnNode = useCallback((nodeId, isDraggedNode = false) => {
      // Skip centering if this is a dragged node
      if (isDraggedNode) return;
      
      if (!reactFlowInstanceRef.current || !nodeId) return;
      
      // Find the selected node
      const selectedNode = nodes.find(node => node.id === nodeId);
      if (!selectedNode) return;
      
      // Check if the node is isolated (no parent and no children)
      const hasParent = selectedNode.data.parent !== undefined && selectedNode.data.parent !== null;
      const hasChildren = selectedNode.data.children && selectedNode.data.children.length > 0;
      const isIsolatedNode = !hasParent && !hasChildren;
      
      if (isIsolatedNode) {
        // For isolated nodes, fit the entire graph to view
        reactFlowInstanceRef.current.fitView({
          padding: 0.2, // Add some padding around the edges
          duration: 800  // Animation duration
        });
        return;
      }
      
      // For connected nodes, continue with the existing behavior
      // Get the current viewport dimensions
      const { width, height } = containerRef.current.getBoundingClientRect();
      
      // Calculate the position to place the node at 30% from the left and 20% from the top
      const leftOffset = width * 0.3; // 30% from the left edge
      const topOffset = height * 0.2; 
      const x = selectedNode.position.x - leftOffset;
      const y = selectedNode.position.y - topOffset;
      
      // Smoothly transition the viewport to center on the node
      reactFlowInstanceRef.current.setViewport(
        {
          x: -x, // Negate because viewport moves in opposite direction
          y: -y,
          zoom: 1.2 // Slightly zoom in for better visibility
        },
        { duration: 800 } // Animation duration in ms
      );
    }, [nodes]);

    // Update onSelectionChange to handle clicking on the grid (no nodes selected)
    const onSelectionChange = useCallback(({ nodes }) => {
        setSelectedNodes(nodes);
        
        if (nodes.length === 1) {
            // If a single node is selected, center the view on it
            centerViewOnNode(nodes[0].id);
        } else if (nodes.length === 0) {
            // If no nodes are selected (clicked on empty space/grid), fit the entire graph
            if (reactFlowInstanceRef.current) {
                reactFlowInstanceRef.current.fitView({
                    padding: 0.2, // Add some padding around the edges
                    duration: 800  // Animation duration
                });
            }
        }
    }, [centerViewOnNode]);
    
    // Store the ReactFlow instance when it's initialized
    const onInit = useCallback((reactFlowInstance) => {
      reactFlowInstanceRef.current = reactFlowInstance;
    }, []);

    return (
        <div 
        className='flows-detail-preview'
        ref={containerRef}
        >
          <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onSelectionChange={onSelectionChange}
              onNodeDrag={onNodeDrag}
              onNodeDragStart={onNodeDragStart}
              onNodeDragStop={onNodeDragStop}
              onInit={onInit}
              connectionMode="loose"
              connectOnClick={false}
              multiSelectionKeyCode="Control"
              deleteKeyCode="Delete"
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              fitView
          >
            <FlowsPreviewPanel 
              nodes={nodes}
              setNodes={setNodes}
              setEdges={setEdges}
              setLayoutType={setLayoutType}
              selectedNodes={selectedNodes}
              selectedAction={selectedAction}
              setSelectedAction={setSelectedAction}
            />

            {/* <FlowsPreviewDetail selectedAction={selectedAction}/> */}

            
              <Background color="darkgrey" gap={16} />
              <MiniMap />
              <Controls 
              showInteractive={false}
              />
          </ReactFlow>
      </div>
    );
}

export default FlowsPreview
